import TableContainerReactTable from "Components/Common/TableContainerReactTable";
import { ProductPriceListItem } from "features/products/types/productPriceListTypes";
import { useMemo } from "react";
import { InteractiveCheckbox } from "../components/InteractiveCheckbox";
import { SimpleEditableCell } from "../components/SimpleEditableCell";
import { EditableDropdownCell } from "./EditableCell";
import { PriceListTableProps } from "../types/PriceTabTypes";
import { RowSelectionState } from "@tanstack/react-table";

export const PriceListTable: React.FC<PriceListTableProps> = ({
  priceListData,
  priceListTaxOptions,
  hasData,
  updatingItems,
  changedCells,
  rowSelection,
  handleCellChange,
  handleTaxFieldChange,
  handleToggleAllowDiscount,
  handleToggleUseVolumePricing,
  onRowSelectionChange,
  handleRowSelect
}) => {


  const columns = useMemo(() => [
    { header: 'Price List Name', accessorKey: 'priceListName', size: 160, enableColumnFilter: false },
    {
      header: 'Purchase Exc', accessorKey: 'unitPurchasePrice', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-unitPurchasePrice`;
        const originalValue = cell.getValue()?.toString() || '';
        const changedCell = changedCells[cellKey];
        const currentValue = changedCell ? changedCell.newValue : originalValue;

        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'unitPurchasePrice', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="unitPurchasePrice"
          />
        );
      }
    },
    {
      header: 'Purchase Inc', accessorKey: 'unitPurchasePriceInc', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-unitPurchasePriceInc`;
        const originalValue = cell.getValue()?.toString() || '';
        const changedCell = changedCells[cellKey];
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'unitPurchasePriceInc', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="unitPurchasePriceInc"
          />
        );
      }
    },
    { header: 'Purchase Cur', accessorKey: 'defaultPurchaseCurrency', size: 100, enableColumnFilter: false },
    {
      header: 'Purchase Tax', accessorKey: 'purchaseTax', size: 120, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        return (
          <EditableDropdownCell
            value={cell.getValue() || ''}
            options={priceListTaxOptions}
            onChange={(newValue) => handleTaxFieldChange(item, 'purchaseTax', newValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
          />
        );
      }
    },
    {
      header: 'Price', accessorKey: 'unitPrice', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-unitPrice`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'unitPrice', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="unitPrice"
          />
        );
      }
    },
    {
      header: 'Price Inc', accessorKey: 'priceInc', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-priceInc`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'priceInc', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="priceInc"
          />
        );
      }
    },
    {
      header: 'Old Price Inc', accessorKey: 'oldPriceInc', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-oldPriceInc`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'oldPriceInc', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="oldPriceInc"
          />
        );
      }
    },
    {
      header: 'Markup %', accessorKey: 'markupPercent', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-markupPercent`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'markupPercent', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="markupPercent"
          />
        );
      }
    },
    {
      header: 'Default Sales Tax', accessorKey: 'defaultTax', size: 120, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        return (
          <EditableDropdownCell
            value={cell.getValue() || ''}
            options={priceListTaxOptions}
            onChange={(newValue) => handleTaxFieldChange(item, 'defaultTax', newValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
          />
        );
      }
    },
    {
      header: 'Allow Disc', accessorKey: 'allowDiscount', size: 80, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        return (
          <InteractiveCheckbox
            checked={cell.getValue()}
            onChange={(newValue) => handleToggleAllowDiscount(item, newValue)}
            disabled={isUpdating}
            title={isUpdating ? 'Updating...' : `Toggle discount ${cell.getValue() ? 'off' : 'on'}`}
          />
        );
      }
    },
    {
      header: 'Use Volume Pricing', accessorKey: 'useVolumePricing', size: 120, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        return (
          <InteractiveCheckbox
            checked={cell.getValue()}
            onChange={(newValue) => handleToggleUseVolumePricing(item, newValue)}
            disabled={isUpdating}
            title={isUpdating ? 'Updating...' : `Toggle volume pricing ${cell.getValue() ? 'off' : 'on'}`}
          />
        );
      }
    },
    {
      header: 'Max Price', accessorKey: 'sellRangeMax', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-sellRangeMax`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'sellRangeMax', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="sellRangeMax"
          />
        );
      }
    },
    {
      header: 'Min Price', accessorKey: 'sellRangeMin', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-sellRangeMin`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'sellRangeMin', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="sellRangeMin"
          />
        );
      }
    },
    {
      header: 'Starting Price', accessorKey: 'startingPrice', size: 100, enableColumnFilter: false,
      cell: (cell: any) => {
        const item = cell.row.original as ProductPriceListItem;
        const isUpdating = updatingItems.has(item.priceListId);
        const cellKey = `${item.rowId}-startingPrice`;
        const changedCell = changedCells[cellKey];
        const originalValue = cell.getValue()?.toString() || '';
        const currentValue = changedCell ? changedCell.newValue : originalValue;
        return (
          <SimpleEditableCell
            key={cellKey}
            value={currentValue}
            onChange={(newValue) => handleCellChange(item.rowId, 'startingPrice', newValue, originalValue)}
            disabled={isUpdating}
            isLoading={isUpdating}
            fieldName="startingPrice"
          />
        );
      }
    },
    { header: 'Description', accessorKey: 'priceListDescription', size: 120, enableColumnFilter: false },
    { header: 'Type', accessorKey: 'type', size: 80, enableColumnFilter: false },
  ], [updatingItems, priceListTaxOptions]);


  return (
    <div className="price-content">
        {hasData ? (
          <TableContainerReactTable
            columns={columns}
            data={priceListData}
            divClass="table-responsive"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            enableRowSelection={true}
            enableMultiRowSelection={false}
            rowSelection={rowSelection}
            onRowSelectionChange={(selection) => onRowSelectionChange(selection as RowSelectionState)}
            onRowClick={handleRowSelect}
            getRowId={(row) => row.rowId.toString()}
            verticalLines={true}
          />
        ) : (
          <div className="text-center py-4">
            <div className="text-muted">
              <i className="ri-database-2-line fs-24 mb-2"></i>
              <p>No price list data available for this product.</p>
              <p className="small">Click "New" to add a price list.</p>
            </div>
          </div>
        )}
      </div>
  );
};